export interface CoatingChecklistMainRow {
  id: string;
  category: string;
  ref: string;
  description: string;
  /** Shown in small text next to description (e.g. ACC). */
  suffix?: string;
}

/**
 * Category whose body renders the DFT layer grid (Layer × Clock × Left/Center/Right) instead of
 * the regular Description/Value items table. Treated as a first-class category — added via
 * "Add Category" or seeded by default.
 */
export const COATING_DFT_HOST_CATEGORY = 'Dry film thickness';

export function isCoatingDftHostCategory(category: string): boolean {
  return category.trim().toLowerCase() === COATING_DFT_HOST_CATEGORY.toLowerCase();
}

/** Flat list with category repeated per row for rowspan rendering. */
export const COATING_CHECKLIST_MAIN_ROWS: CoatingChecklistMainRow[] = [];

export const COATING_DFT_CLOCKS = ['12', '3', '6', '9'] as const;
export const COATING_DFT_SUB = ['Left', 'Center', 'Right'] as const;

/** Distinct category labels in checklist order (for section dropdowns). */
export const COATING_CATEGORY_OPTIONS: string[] = (() => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of COATING_CHECKLIST_MAIN_ROWS) {
    if (!seen.has(row.category)) {
      seen.add(row.category);
      out.push(row.category);
    }
  }
  return out;
})();

export interface CoatingChecklistTemplateItem {
  id: string;
  description: string;
  /** Free-text "Note" column (also used as a hint suffix next to the description). */
  suffix?: string;
  criteria?: 'Accept/Reject/NA' | 'Text' | 'Value' | 'Table' | 'Dateandtime';
  unit?: string;
  comparator?: string;
  minValue?: string;
  maxValue?: string;
  value?: string;
  textValue?: string;
  dateTimeValue?: string;
  checkboxRequired?: boolean;
  fileUploadRequired?: boolean;
  tableData?: {
    rows: Array<{
      name: string;
      values: string[];
      unit?: string;
      comparator?: string;
      minValue?: string;
      maxValue?: string;
    }>;
    columns: string[];
    mode?: 'single' | 'multiple';
  };
}

export interface CoatingChecklistTemplateSection {
  id: string;
  /** Label from {@link COATING_CATEGORY_OPTIONS} (dropdown). */
  category: string;
  items: CoatingChecklistTemplateItem[];
}

export interface CoatingChecklistTemplateDoc {
  id: string;
  name: string;
  sections: CoatingChecklistTemplateSection[];
}

export function newCoatingChecklistId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Sections only — same grouping as {@link createSeedCoatingTemplate} (for single-checklist UI). */
export function createSeedChecklistSections(): CoatingChecklistTemplateSection[] {
  const sections: CoatingChecklistTemplateSection[] = [];
  for (const row of COATING_CHECKLIST_MAIN_ROWS) {
    const item: CoatingChecklistTemplateItem = {
      id: row.id,
      description: row.description,
      suffix: row.suffix,
      criteria: row.description.toLowerCase().includes('date/time') ? 'Dateandtime' : 'Accept/Reject/NA',
      value: row.description.toLowerCase().includes('date/time') ? '' : 'N/A',
    };
    const last = sections[sections.length - 1];
    if (last && last.category === row.category) {
      last.items.push(item);
    } else {
      sections.push({
        id: newCoatingChecklistId('sec'),
        category: row.category,
        items: [item],
      });
    }
  }
  return sections;
}

/** One checklist template seeded from the static demo rows (same items, grouped by category). */
export function createSeedCoatingTemplate(name = 'Template 1'): CoatingChecklistTemplateDoc {
  return {
    id: newCoatingChecklistId('tpl'),
    name,
    sections: createSeedChecklistSections(),
  };
}
